import { CookieConsentService } from '@inter-ikea-kompis/services';
import type { ICookieConsentServiceOptions } from '@inter-ikea-kompis/types';

import type { ICustomData, IItems, IModuleClient } from '@insights/insights-data-provider';
import type { InsightsApi, ModuleClient } from '@insights/insights-data-provider';

import type IAnalyticsPropContext from '../types/IAnalyticsPropContext.js';
import type IAnalyticsReporter from '../types/IAnalyticsReporter.js';
import ReporterManager from '../utilities/ReporterManager.js';

/**
 * Provides the connection between a tracker and the Insights DPC.
 */
export default class InsightsReporter implements IAnalyticsReporter {
	private moduleClient: ModuleClient | null = null;
	private cookieConsentService: CookieConsentService | null = null;

	/**
	 * Connects the reporter to the Insights DPC.
	 *
	 * @param insightsApi ...
	 * @param moduleMetaData ...
	 * @param cookieConsentServiceOptions ...
	 */
	public async connect(
		insightsApi: InsightsApi | null,
		moduleMetaData: IModuleClient,
		cookieConsentServiceOptions: ICookieConsentServiceOptions
	): Promise<void> {
		// We consider the reporter connected regardless of if the module client was created or not.
		// We connect first, before any async stuff, to allow disconnecting while any async stuff is ongoing.
		ReporterManager.connect(this);

		this.cookieConsentService = new CookieConsentService(cookieConsentServiceOptions);
		let hasCookieConsent;
		try {
			hasCookieConsent = await this.cookieConsentService?.hasCookieConsent(2);
		} catch (error) {
			console.error('hasCookieConsent error thrown when trying to connect InsightsReporter');
			return;
		}

		if (hasCookieConsent) {
			if (!insightsApi) {
				throw Error(
					`InsightsApi is not found, did you forget to run AnalyticsManager.setInsightsApi before rendering ${moduleMetaData.moduleId}?`
				);
			}

			if (!insightsApi.isEnabled) {
				throw Error(
					`InsightsApi is not enabled, did you forget to run insightsApi.connectApplication before rendering ${moduleMetaData.moduleId}?`
				);
			}
			this.moduleClient = insightsApi.moduleClient(moduleMetaData);
		}
	}

	/**
	 * Returns whether the reporter has been connected or not.
	 * It is still considered connected even if no module client could be created when trying to connect.
	 *
	 * @returns Whether the reporter has been connected or not.
	 */
	public isConnected(): boolean {
		return ReporterManager.isConnected(this);
	}

	/**
	 * Disconnects the reporter.
	 */
	public disconnect(): void {
		ReporterManager.disconnect(this);
	}

	/**
	 * Get the id of the the reporter, if connected. See {@link ReporterManager.getConnectedId}.
	 */
	public getConnectedId(): string | null {
		return ReporterManager.getConnectedId(this);
	}

	/**
	 * Report a custom event to InsightsApi.
	 *
	 * @param event ...
	 */
	public report(event: ICustomData): Promise<void> {
		return this.reportIfCookieConsent(() => this.moduleClient?.sendEvent(event));
	}

	public addToCart(data: IItems): Promise<void> {
		return this.reportIfCookieConsent(() => this.moduleClient?.addToCart(data));
	}

	public addToWishList(data: IItems): Promise<void> {
		return this.reportIfCookieConsent(() => this.moduleClient?.addToWishList(data));
	}

	public saveDesign(
		options: { type: 'Save' } | { type: 'Share'; vpcCode?: string },
		context?: IAnalyticsPropContext['context']
	): Promise<void> {
		const { type } = options;
		const vpcCode = 'vpcCode' in options ? options.vpcCode : undefined;
		const payload = {
			type,
			context
		};
		return this.reportIfCookieConsent(() => this.moduleClient?.saveDesign(vpcCode, payload));
	}

	/**
	 * @param sendFunction Function to perform if the user has cookie consent.
	 */
	private async reportIfCookieConsent(sendFunction: () => void): Promise<void> {
		const hasCookieConsent = await this.cookieConsentService?.hasCookieConsent(2);
		if (hasCookieConsent) {
			sendFunction();
		}
	}
}
